import { API_BASE_URL } from "../config";
import { DB } from './indexedDB.js';

const LatestUserReportscacheKey = 'latest-user-reports';

/**
 * Build valid URL params string
 * @param {Object} params URI params
 * @returns {String}
 */
function buildQueryString(params) {
    return Object.keys(params)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
        .join('&');
}

/**
 * Fetch consumables
 * @param {String} token the app Authorization token
 * @param {String} search the search param value
 * @param {AbortController} signal 
 * @returns {Promise<Any>} fetch result
 */
export const fetchConsumables = async (token, search, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/consumables/all?search=${encodeURIComponent(search)}`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal, // Pass the signal to fetch
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Mark notification as readed
 * @param {string} token the auth token
 * @param {string} uuid the notification id/uuid
 * @param {AbortController} signal 
 */
export const markNotificationAsReaded = async (token, uuid, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/notifications/markasread/${uuid}`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            method: 'POST',
            signal: signal,
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Send notifications to multiple users
 * @param {string} token the auth token
 * @param {object} data the payload data
 * @param {AbortController} signal 
 */
export const sendNotifications = async (token, data, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/notifications/sendtousers`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            signal: signal,
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Delete a notification
 * @param {string} token the auth token
 * @param {string} notificationId the notification ID
 * @param {AbortController} signal 
 * @returns {Promise<Any>} fetch result
 */
export const deleteNotification = async (token, notificationId, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal,
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Fetch valid recipients for notifications
 * @param {string} token the auth token
 * @param {AbortController} signal 
 * @returns {Promise<Any>} fetch result
 */
export const fetchRecipients = async (token, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/notifications/valid-recipients`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal,
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Fetch staff hours categories
 * 
 * @param {string} token The API token
 * @param {AbortController} signal
 * @returns {Promise} The fetch result
 */
export const fetchStaffHoursCategories = async (token, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/staff/hours/categories`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal, // Pass the signal to fetch
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
}

/**
 * Fetch species
 * 
 * @param {string} token The API token
 * @param {AbortController} signal
 * @returns {Promise} The fetch result
 */
export const fetchSpecies = async (token, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/works/extras/forestal/species/all`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal, // Pass the signal to fetch
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Fetch extra actions
 * 
 * @param {string} token The API token
 * @param {AbortController} signal
 * @returns {Promise} The fetch result
 */
export const fetchExtraActions = async (token, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/works/extras/forestal/actions/all`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal, // Pass the signal to fetch
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Fetch extra statuses
 * 
 * @param {string} token The API token
 * @param {AbortController} signal
 * @returns {Promise} The fetch result
 */
export const fetchExtraStatuses = async (token, signal) => {
    try {
        const response = await fetch(`${API_BASE_URL}/works/extras/forestal/status/all`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal, // Pass the signal to fetch
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Fetch user work reports
 * 
 * @param {string} token The API token
 * @param {object} params Optional params for the request
 * @param {AbortController} signal 
 * @param {boolean} skipCache If true, the cache will not be used
 */
export const fetchReports = async (token, params, signal, skipCache = false) => {
    const queryString = buildQueryString(params);
    const apiUrl = `${API_BASE_URL}/works/workdays/reports?${queryString}`;

    try {

        if (!skipCache) {
            // If app is not online, try to get the data from the cache
            if (!navigator.onLine) {
                const cachedData = await DB.getItem(LatestUserReportscacheKey);
                if (cachedData) {
                    console.warn('Offline: Fetched reports data from cache.');
                    return cachedData;
                }
            }

            // If online, but data is in cache and has not expired, return it
            if (navigator.onLine) {
                const cachedData = await DB.getItem(LatestUserReportscacheKey);
                if (cachedData && cachedData.expiry > Date.now()) {
                    console.info('Fetched reports data from cache.');
                    return cachedData;
                }
            }
        }

        // Is online and data is not in cache or has expired, make API call
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal, // Pass the signal to fetch
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        // Ok
        const data = await response.json();

        // Store result in cache. Expire it after 1 minute
        const cacheExpires = new Date();
        cacheExpires.setMinutes(cacheExpires.getMinutes() + 1);
        data.expiry = cacheExpires.getTime();

        // Store in IndexedDB
        try {
            await DB.cleanupExpired(); // Clean up expired entries first
            await DB.setItem(LatestUserReportscacheKey, data);
            console.log('Data stored in IndexedDB successfully');
        } catch (storageError) {
            console.warn('IndexedDB storage failed:', storageError);
            // Continue execution - the function will still return data
        }

        return data;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            console.error(error);
            throw error;
        }
    }
};

/**
 * Fetch reports by planning ID
 * 
 * @param {string} token The API token
 * @param {number} planningId The planning ID
 * @param {object} params Optional params for the request
 * @param {AbortController} signal 
 * @returns {Promise<Any>} fetch result
 */
export const fetchReportsByPlanning = async (token, planningId, params = {}, signal) => {
    const queryString = buildQueryString(params);
    const apiUrl = `${API_BASE_URL}/works/workdays/reports/by/planning/${planningId}?${queryString}`;

    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal,
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Sends workday data to crate a new report on the database for a given work.
 * 
 * @param {string} token the app auth token
 * @param {Object} data the payload data
 * @param {AbortController} signal 
 * @returns the API call result
 */
export const createWorkReport = async (token, data, signal = null) => {
    try {
        const response = await fetch(`${API_BASE_URL}/works/workdays/reports/create`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
            ...(signal ? { signal } : {})
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} message: ${response.message}`);
        }

        const payload = await response.json();

        // Invalidate cached list to avoid stale data after creating a report
        try {
            await DB.removeItem(LatestUserReportscacheKey);
        } catch (cacheError) {
            console.warn('Failed to invalidate reports cache after create:', cacheError);
        }

        return payload;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch aborted');
        } else {
            console.error('Fetch error:', error);
        }
        throw error;
    }
};

/**
 * Sends workday data to update an existing report on the database for a given work.
 * 
 * @param {string} token the app auth token
 * @param {Object} data the payload data
 * @param {AbortController} signal 
 * @returns the API call result
 */
export const updateWorkReport = async (token, data, signal = null) => {
    try {
        data.pwa = true; // Signal the server that this request comes from the PWA

        let apiUrl = `${API_BASE_URL}/works/workdays/reports/${data.id}/update`;

        const response = await fetch(apiUrl, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
            ...(signal ? { signal } : {})
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} message: ${response.message}`);
        }

        const payload = await response.json();

        // Invalidate cached list to avoid stale data after updating a report
        try {
            await DB.removeItem(LatestUserReportscacheKey);
        } catch (cacheError) {
            console.warn('Failed to invalidate reports cache after update:', cacheError);
        }

        return payload;
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch aborted');
        } else {
            console.error('Fetch error:', error);
        }
        throw error;
    }
};

/**
 * Try to get a work report from the cache
 * 
 * @param {number} reportId The report ID
 * @returns {object} The report or undefined
 */
export const tryToGetWorkReportFromCache = async (reportId) => {

    const cachedData = await DB.getItem(LatestUserReportscacheKey);

    if (cachedData) {
        if (Object.hasOwn(cachedData, 'reports')) {
            return cachedData.reports.find((report) => report.id == reportId);
        }
    }

    return undefined;
};

/**
 * Get a work report by ID
 * @param {String} token the app Authorization token
 * @param {Number} id the work report ID
 * @param {AbortController} signal 
 * @param {Object} params Optional params for the request
 * @returns {Promise<Any>} fetch result
 */
export const getWorkReport = async (token, id, signal, params = {}) => {

    const doTransformation = (report) => {
        if (report) {
            // If employees are not present or empty but hours are, add them
            if (!report.employees || report.employees.length === 0 && report.hours > 0) {
                report.employees = report.hours; //[...report.hours];
                report.employees.forEach((employee) => {
                    employee.sid = employee.sid || employee.staff_id;

                    employee.workServiceId = employee.workServiceId ||
                        (Array.isArray(employee.catalog_servicies) && employee.catalog_servicies.length > 0
                            ? employee.catalog_servicies[0].id
                            : null);

                    employee.workActionAreaId = employee.workActionAreaId ||
                        (Array.isArray(employee.work_action_areas) && employee.work_action_areas.length > 0
                            ? employee.work_action_areas[0].id
                            : null);

                    employee.workActionSubareaId = employee.workActionSubareaId ||
                        (Array.isArray(employee.work_action_subareas) && employee.work_action_subareas.length > 0
                            ? employee.work_action_subareas[0].id
                            : null);

                    employee.hoursCategoryId = employee.hoursCategoryId || employee.hours_category_id;
                });

            }

            // Consumptions

            // Extras forestal
            if (!report.extras || report.extras.length === 0 && report.extras_forestal.length > 0) {
                report.extras = report.extras_forestal;
                report.extras.forEach((extra) => {
                    extra.workActionAreaId = extra.workActionAreaId ||
                        (Array.isArray(extra.work_action_areas) && extra.work_action_areas.length > 0
                            ? extra.work_action_areas[0].id
                            : null);

                    extra.workActionSubareaId = extra.workActionSubareaId ||
                        (Array.isArray(extra.work_action_subareas) && extra.work_action_subareas.length > 0
                            ? extra.work_action_subareas[0].id
                            : null);
                });
            }
        }

        return report;
    };

    try {
        // Try to load it from cache
        const cachedReport = await tryToGetWorkReportFromCache(id);
        if (cachedReport) {
            console.info('Fetched report from cache with ID:', id);
            return {
                report: doTransformation(cachedReport)
            };
        } else {
            console.info('Report with ID', id, 'not found in cache. Fetching from API...');
        }

        let apiUrl = `${API_BASE_URL}/works/workdays/reports/${id}`;

        if (Object.keys(params).length > 0) {
            apiUrl += `?${buildQueryString(params)}`;
        }

        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} message: ${response.message}`);
        }

        let payload = await response.json();
        return doTransformation(payload);
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch aborted');
        } else {
            console.error('Fetch error:', error);
        }
        throw error;
    }
};

/**
 * Download a file
 * @param {String} token the app Authorization token
 * @param {String} url the file url
 */
export const downloadFile = async (token, url, fileName) => {
    try {
        // Fetch the file as a Blob using an authenticated request
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });

        if (!response.ok) {
            throw new Error(`Error fetching file: ${response.statusText}`);
        }

        const blob = await response.blob();

        // Create a URL for the Blob and download it
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        console.error('Error downloading file:', error);
    }
};

/**
 * Get a list of vehicles
 * 
 * @param {string} token the app Authorization token
 * @param {AbortController} signal 
 * @param {object} params Optional params for the request
 * @returns {Promise<Any>} fetch result
 * @throws {Error} if the request fails
 */
export const fetchVehicles = async (token, signal, params = {}) => {
    try {
        const response = await fetch(`${API_BASE_URL}/vehicles/vehicles`, {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            signal: signal,
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch request was aborted');
        } else {
            throw error;
        }
    }
};

/**
 * Send a workshop alert to the server
 * 
 * @param {string} token the app Authorization token
 * @param {object} data the payload data
 * @param {AbortController} signal 
 * @returns {Promise<Any>} fetch result
 * @throws {Error} if the request fails
 */
export const sendWorkshopAlert = async (token, data, signal = null) => {
    try {
        const headers = {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
        };

        let body;

        if (data.is_pwa) {
            const formData = new FormData();
            formData.append('is_pwa', '1');
            formData.append('cause', data.cause);
            formData.append('asset_type', data.asset_type);
            data.asset_ids.forEach((id, index) => {
                formData.append(`asset_ids[${index}]`, id);
            });
            if (data.photos?.length) {
                data.photos.forEach((photo, index) => {
                    if (photo.file) {
                        formData.append(`photos[${index}]`, photo.file, photo.file.name);
                    }
                });
            }
            body = formData;
        } else {
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify(data);
        }

        const response = await fetch(`${API_BASE_URL}/garages/workshop-alerts`, {
            method: 'POST',
            headers,
            body,
            ...(signal ? { signal } : {})
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} message: ${response.message}`);
        }

        return await response.json();
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Fetch aborted');
        } else {
            console.error('Fetch error:', error);
        }
        throw error;
    }
};